import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EMAIL_STATUSES, type CrmDeriveResult, type CrmEmailEvent, type CrmPerson } from '@aviaconsole/shared';
import { capi } from '../../api/client';
import { useI18n, type Key } from '../../i18n';
import { formatDate } from '../../lib/format';
import { useToast } from '../../components/Toast';
import { Button, Chip, Input, Modal } from '../../components/ui';
import { crmLabel, emailStatusTone } from '../../lib/crm';

/**
 * Every address of one person with its status, where it came from and what
 * happened to it: add one, derive candidates from the account's domain, make
 * one primary, lock it against the automatic logic, set a status by hand.
 */
export function PersonEmails({ personId, name, onClose, onChange }: { personId: string; name: string; onClose: () => void; onChange: () => void }) {
  const { t, lang } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();
  const person = useQuery({ queryKey: ['crm-person', personId], queryFn: () => capi<CrmPerson>(`/crm/people/${personId}`) });
  const events = useQuery({ queryKey: ['crm-person-email-events', personId], queryFn: () => capi<CrmEmailEvent[]>(`/crm/people/${personId}/emails/events`) });
  const [address, setAddress] = useState('');
  const [history, setHistory] = useState(false);
  const done = (p?: CrmPerson) => {
    if (p) qc.setQueryData(['crm-person', personId], p); else qc.invalidateQueries({ queryKey: ['crm-person', personId] });
    qc.invalidateQueries({ queryKey: ['crm-person-email-events', personId] });
    onChange();
  };
  const fail = (e: Error) => toast.push(e.message, 'error');
  const enc = encodeURIComponent;

  const add = useMutation({
    mutationFn: () => capi<CrmPerson>(`/crm/people/${personId}/emails`, { method: 'POST', json: { address } }),
    onSuccess: (p) => { setAddress(''); done(p); }, onError: fail,
  });
  const patch = useMutation({
    mutationFn: ({ a, body }: { a: string; body: Record<string, unknown> }) => capi<CrmPerson>(`/crm/people/${personId}/emails/${enc(a)}`, { method: 'PATCH', json: body }),
    onSuccess: (p) => done(p), onError: fail,
  });
  const remove = useMutation({
    mutationFn: (a: string) => capi<CrmPerson>(`/crm/people/${personId}/emails/${enc(a)}`, { method: 'DELETE' }),
    onSuccess: (p) => done(p), onError: fail,
  });
  const derive = useMutation({
    mutationFn: () => capi<CrmDeriveResult & { person: CrmPerson }>(`/crm/people/${personId}/emails/derive`, { method: 'POST', json: {} }),
    onSuccess: (r) => {
      done(r.person);
      toast.push(r.skipped ? t('emDeriveSkipped', { why: t(`emSkip_${r.skipped}` as Key) }) : t('emDerived', { n: r.created.length }));
    },
    onError: fail,
  });

  const p = person.data;
  return (
    <Modal title={t('emTitle', { name })} onClose={onClose}>
      <div className="stack md">
        <table className="small"><tbody>
          {p && p.emails.length === 0 && <tr className="empty"><td colSpan={9}>{t('emNone')}</td></tr>}
          {p?.emails.map((e) => (
            <tr key={e.address}>
              <td>
                <div style={{ fontWeight: 600 }}>
                  {e.address} {e.primary && <Chip tone="primary" icon="fas fa-star">{t('emPrimary')}</Chip>} {e.locked && <span title={t('emLocked')}><i className="fas fa-lock muted" aria-label={t('emLocked')} /></span>}
                </div>
                <div className="sub">{e.source} · {t('emSends', { sends: e.sends, bounces: e.bounces, replies: e.replies })}{e.smtpCode ? ` · ${e.smtpCode} ${e.smtpText ?? ''}` : ''}</div>
              </td>
              <td><Chip tone={emailStatusTone(e.status)}>{crmLabel('emailStatus', e.status, lang)}</Chip></td>
              <td className="r"><span className="row" style={{ justifyContent: 'flex-end' }}>
                {!e.primary && e.status !== 'rejected' && <button type="button" className="linkbtn" onClick={() => patch.mutate({ a: e.address, body: { primary: true } })}>{t('emMakePrimary')}</button>}
                <button type="button" className="linkbtn" onClick={() => patch.mutate({ a: e.address, body: { locked: !e.locked } })}>{e.locked ? t('emUnlock') : t('emLock')}</button>
                <select className="inline" aria-label={t('emSetStatus')} value="" onChange={(ev) => ev.target.value && patch.mutate({ a: e.address, body: { status: ev.target.value } })}>
                  <option value="">{t('emSetStatus')}</option>
                  {EMAIL_STATUSES.filter((s) => s !== e.status).map((s) => <option key={s} value={s}>{crmLabel('emailStatus', s, lang)}</option>)}
                </select>
                <button type="button" className="linkbtn" style={{ color: 'var(--danger)' }} onClick={() => remove.mutate(e.address)}>{t('emRemove')}</button>
              </span></td>
            </tr>
          ))}
        </tbody></table>
        <form className="row" onSubmit={(ev) => { ev.preventDefault(); if (address.trim()) add.mutate(); }}>
          <Input type="email" placeholder="naam@bedrijf.nl" aria-label={t('emAdd')} value={address} onChange={(e) => setAddress(e.target.value)} />
          <Button type="submit" icon="fas fa-plus" disabled={add.isPending}>{t('emAdd')}</Button>
          <Button icon="fas fa-wand-magic-sparkles" onClick={() => derive.mutate()} disabled={derive.isPending}>{t('emDerive')}</Button>
        </form>
        <div>
          <button type="button" className="linkbtn" onClick={() => setHistory(!history)}>{t('emHistory')} ({events.data?.length ?? 0})</button>
          {history && (
            <table className="small" style={{ marginTop: '0.5rem' }}><tbody>
              {events.data?.map((ev) => (
                <tr key={ev.id}>
                  <td className="muted" style={{ whiteSpace: 'nowrap' }}>{formatDate(ev.occurredAt, lang)}</td>
                  <td>{ev.address ?? '—'}</td>
                  <td>{ev.source} · {ev.signal}</td>
                  <td>{ev.fromStatus || ev.toStatus ? `${ev.fromStatus ?? '∅'} → ${ev.toStatus ?? '∅'}` : ''}{!ev.applied && ev.toStatus ? ' ·' : ''}</td>
                  <td className="sub">{ev.detail}</td>
                </tr>
              ))}
            </tbody></table>
          )}
        </div>
        <div className="row" style={{ justifyContent: 'flex-end' }}><Button onClick={onClose}>{t('segClose')}</Button></div>
      </div>
    </Modal>
  );
}
